import { Box, Button, Flex, Icon, IconButton, List, ListItem, Text, Tooltip, useDisclosure } from '@chakra-ui/react';
import { ColumnDef, Table as TableType } from '@tanstack/react-table';
import debounce from 'lodash/debounce';
import { useCallback, useMemo, useRef, useState } from 'react';
import { Link, Link as RouterLink } from 'react-router-dom';

import { IconTrashbin, IconView } from 'assets/icons';
import { ConfirmationModal } from 'components/Modal';
import SearchField from 'components/SearchField';
import TanstackTable from 'components/TanstackTable';
import IndeterminateCheckbox from 'components/TanstackTable/IndeterminateCheckbox';
import Title from 'components/Title';
import { routePaths } from 'global/routePaths';
import { ICustomFieldItem, ICustomFormSearchQuery } from 'hooks/customField/useFetchPaginatedCustomFields';
import { useDeleteCFReportsTemplates } from 'hooks/reports/customFields/useDeleteCFReportsTemplates';
import { useFetchPaginatedCFReportsTemplates } from 'hooks/reports/customFields/useFetchPaginatedCFReportsTemplates';
import { formatYearMonthDay } from 'utils/format';
import { normalizeDate } from 'utils/helpers';

import TableFooter from './TableFooter';

const initialSearchQuery: ICustomFormSearchQuery = {
  page: 1,
  page_size: 25,
  search: '',
};

const CustomFieldReportTemplates = () => {
  const tableInstanceRef = useRef<TableType<any> | null>(null);

  const [searchQuery, setSearchQuery] = useState({ ...initialSearchQuery });
  const [dialogIds, setDialogIds] = useState<{ template_ids: Array<string> }>({ template_ids: [] });

  const { isOpen: isDeleteModalOpen, onOpen: onDeleteModalOpen, onClose: onDeleteModalClose } = useDisclosure();

  const { refetch, status, data } = useFetchPaginatedCFReportsTemplates(searchQuery);
  const { status: deleteCFTemplatesStatus, mutateAsync: mutateAsyncDeleteCustomFields } = useDeleteCFReportsTemplates();

  const columns = useMemo<ColumnDef<any>[]>(() => {
    return [
      {
        id: 'select',
        size: 46,
        header: ({ table }) => (
          <IndeterminateCheckbox
            {...{
              checked: table.getIsAllRowsSelected(),
              indeterminate: table.getIsSomeRowsSelected(),
              onChange: table.getToggleAllRowsSelectedHandler(),
            }}
          />
        ),
        cell: ({ row }) => (
          <IndeterminateCheckbox
            {...{
              checked: row.getIsSelected(),
              disabled: !row.getCanSelect(),
              indeterminate: row.getIsSomeSelected(),
              onChange: row.getToggleSelectedHandler(),
            }}
          />
        ),
      },

      {
        accessorKey: 'template_name',
        id: 'name',
        header: 'Name',
        size: 150,
      },
      {
        accessorKey: 'template_parameters.user',
        id: 'userName',
        header: 'User/Group',
        size: 150,
        cell: info => {
          const user = info?.row?.original?.template_parameters?.user?.name;
          const group = info?.row?.original?.template_parameters?.group?.name;
          return (
            <Box display="flex" flexDirection="column">
              {user ? user : ''}
              {group ? group : ''}
            </Box>
          );
        },
      },
      {
        accessorKey: 'template_parameters.job',
        header: 'Job/Task',
        cell: info => {
          return (
            <Box display="flex" flexDirection="column">
              {info?.row?.original?.template_parameters?.job?.name || '- '}
              {info?.row?.original?.template_parameters?.task?.name ? (
                <Box>{'/' + info?.row?.original?.template_parameters?.task?.name}</Box>
              ) : null}
            </Box>
          );
        },
      },
      {
        accessorKey: 'template_parameters.start_date',
        header: 'Date Range',
        size: 210,
        cell: info => {
          const startDate = normalizeDate(info?.row?.original?.template_parameters?.start_date);
          const endDate = normalizeDate(info?.row?.original?.template_parameters?.end_date);

          return (
            <Flex gridGap="1" flexWrap="wrap">
              <Text fontWeight="semibold" fontSize="sm" color="#1C2045" as="span">
                {startDate ? formatYearMonthDay(startDate) : '- '}
              </Text>
              -
              <Text fontWeight="semibold" fontSize="sm" color="#1C2045" as="span">
                {endDate ? formatYearMonthDay(endDate) : '- '}
              </Text>
            </Flex>
          );
        },
      },
      {
        id: 'customFields',
        header: 'Custom Fields',
        size: 250,
        cell: info => {
          const customFields = info?.row?.original?.template_parameters?.custom_fields;

          return Array.isArray(customFields) && customFields.length ? (
            <List>
              {customFields.map((customField: any) => {
                return (
                  <ListItem key={customField.id}>
                    {customField.field_name}:
                    <b>{Array.isArray(customField.value) ? customField.value.join(', ') : customField.value}</b>
                  </ListItem>
                );
              })}
            </List>
          ) : (
            '-'
          );
        },
      },
      {
        id: 'action',
        header: 'Action',
        size: 210,
        cell: ({ row }) => (
          <Box>
            <Tooltip label="View Template and Generate Report" hasArrow bg="black">
              <IconButton
                as={RouterLink}
                to={routePaths.reports.customFields.templateById.replace(':id', row.original.id)}
                aria-label="View Template"
                background="rgba(38, 103, 201, 0.1)"
                _hover={{ bg: 'rgba(38, 103, 201, 0.2)' }}
                _active={{ bg: 'rgba(38, 103, 201, 0.2)' }}
                m="1"
                icon={<Icon as={IconView} sx={{ '& path': { fill: 'primary.600' } }} color="primary.600" />}
                size="sm"
              />
            </Tooltip>
            <Tooltip label="Delete template" hasArrow bg="black">
              <IconButton
                aria-label="Delete template"
                bg="rgba(255, 83, 83, 0.1)"
                icon={<Icon as={IconTrashbin} />}
                size="sm"
                m="1"
                _hover={{ bg: 'rgba(255, 83, 83, 0.2)' }}
                onClick={() => handleSingleDelete(row.original)}
              />
            </Tooltip>
          </Box>
        ),
      },
    ];
  }, []);

  const handleTableInstanceCreated = (tableInstance: TableType<any>) => {
    tableInstanceRef.current = tableInstance;
  };

  const handleSingleDelete = (rowData: ICustomFieldItem) => {
    setDialogIds({ template_ids: [rowData.id] });
    onDeleteModalOpen();
  };

  const handleBulkDelete = (selectedRowIds: Array<string>) => {
    setDialogIds({ template_ids: selectedRowIds });
    onDeleteModalOpen();
  };

  const deleteCFTemplates = () => {
    mutateAsyncDeleteCustomFields(dialogIds).then(() => {
      refetch();
      onDeleteModalClose();
      setDialogIds({ template_ids: [] });
      tableInstanceRef?.current?.resetRowSelection();
    });
  };

  const handleSearchInputChange = useCallback(
    debounce((e: React.ChangeEvent<HTMLInputElement>) => {
      const searchValue = e.target.value.toLowerCase().trim();
      setSearchQuery({ ...searchQuery, page: 1, search: searchValue });
    }, 400),
    [searchQuery]
  );

  const handlePageChange = useCallback((selectedPageIndex: number) => {
    setSearchQuery(previousSearchQuery => ({
      ...previousSearchQuery,
      page: selectedPageIndex + 1,
    }));
  }, []);

  const handlePageSizeChange = useCallback((pageSize: number) => {
    setSearchQuery(queries => ({ ...queries, page_size: pageSize, page: 1 }));
  }, []);

  return (
    <div>
      <Flex justifyContent="space-between" flexDirection={{ base: 'column', md: 'row' }} mb="5">
        <Title hideBackButton>Custom Field Report Templates</Title>
        <Button
          as={Link}
          colorScheme="secondary"
          mt={{ base: '5', md: '0' }}
          to={routePaths.reports.customFields.newTemplate}
        >
          Create New Template
        </Button>
      </Flex>
      <ConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={onDeleteModalClose}
        title={'Are you sure you want to delete the selected reports template(s)?'}
        subTitle={'You will still be able to restore the reports template(s) again.'}
        confirmButtonLabel="Delete"
        confirmButtonProps={{
          bg: 'rgba(72,82, 175, 0.1)',
          color: 'primary.500',
          _hover: {
            bg: 'rgba(72,82, 175, 0.2)',
          },
          isLoading: deleteCFTemplatesStatus === 'loading',
          onClick: deleteCFTemplates,
        }}
      />

      <Box ml="auto" width="100%" maxWidth={{ lg: '350px' }}>
        <SearchField
          inputProps={{
            placeholder: 'Search Fields',
            onChange: handleSearchInputChange,
          }}
        />
      </Box>
      <TanstackTable
        isLoading={status === 'loading'}
        columns={columns}
        data={data?.payload || []}
        enableRowSelect={true}
        paginationData={data?.meta_data?.pagination}
        onPageChange={handlePageChange}
        handlePageSizeChange={handlePageSizeChange}
        onTableInstanceCreated={handleTableInstanceCreated}
        emptyTableStateProps={{ searchText: searchQuery.search }}
        renderTableFooter={(selectedIds, toggleAllRowSelected) => (
          <TableFooter
            selectedRowIds={selectedIds}
            toggleAllRowSelected={toggleAllRowSelected}
            handleBulkDelete={handleBulkDelete}
          />
        )}
      />
    </div>
  );
};

export default CustomFieldReportTemplates;
